import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

/**
 * A stripe across the top of anything that is not production.
 *
 * Staging is a faithful copy — same code, same screens, sometimes data
 * restored from production — and that is exactly what makes it dangerous. Two
 * systems that look identical are two systems somebody will eventually
 * confuse: taking a real customer's booking in staging where the next refresh
 * wipes it, or showing the owner staging figures as the month's takings.
 *
 * **It reads `/healthz`, not the session, and that is the point.** It used to
 * take the environment from the signed-in session, which meant the one screen
 * without a session — the sign-in page — was also the one screen with no
 * banner. That is precisely the moment the warning is worth having: just
 * before somebody types the owner's real password into the wrong one of two
 * identical doors.
 *
 * The environment cannot be baked into the bundle either: the *same* built
 * bundle is deployed to staging and then promoted to production, which is the
 * whole point of testing a release, so a compiled-in value would be wrong for
 * one of them.
 */
export function EnvironmentBanner(): ReactElement | null {
  const [environment, setEnvironment] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetch('/healthz', { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { environment?: string } | null) => {
        if (!cancelled && body?.environment !== undefined) setEnvironment(body.environment);
      })
      // Silent on purpose. A banner that cannot reach the server has nothing
      // useful to say, and an error here would be noise in front of a sign-in
      // page that is about to fail for a reason worth reading.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (environment === null || environment === 'production') return null;

  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 bg-attention px-3 py-1 text-center text-micro font-semibold uppercase tracking-wide text-ink"
    >
      <span aria-hidden="true">▲</span>
      <span>{environment} — not the real system. Nothing here is a real booking.</span>
    </div>
  );
}
